import type { TFunction } from 'i18next';
import {
  PermissionKey,
  isRbacManagementPermissionKey,
} from '@evimo/types/enums/permission-key';
import {
  isEditableSystemRoleName,
  isHiddenSystemRoleName,
  isSystemRoleName,
  SystemRoleName,
} from '@evimo/types/enums/system-role-name';
import type { PermissionDto } from '~/app/services/authorization/permission.service';
import type {
  RoleDto,
  RoleWithPermissionsDto,
} from '~/app/services/authorization/role.service';
import type { GroupedPermissions, PermissionCategory } from './types';

export const PERMISSION_CATEGORY_ORDER: PermissionCategory[] = [
  'rbacManagement',
  'users',
  'assets',
  'sites',
  'zones',
  'incidents',
  'reports',
  'documents',
  'customFields',
  'partners',
  'maintenance',
  'diagnosticRules',
  'knowledgeBase',
  'platform',
];

const PLATFORM_PREFIXES = new Set(['org-relationships', 'organizations']);

/** Knowledge-base catalog permission key prefixes (platform SUPER_ADMIN catalog). */
const KNOWLEDGE_BASE_PREFIXES = new Set([
  'asset-types',
  'pipeline-debug',
  'manual-log-upload',
  'manufacturers',
  'asset-models',
  'asset-type-manufacturer-models',
  'subsystems',
  'component-classes',
  'component-types',
  'event-states',
  'parameter-names',
  'asset-type-subsystems',
  'asset-subsystem-component-classes',
  'asset-subsystem-component-class-component-types',
]);

export function getPermissionCategory(key: PermissionKey): PermissionCategory {
  if (isRbacManagementPermissionKey(key)) {
    return 'rbacManagement';
  }

  const [prefix] = key.split(':');

  if (prefix === 'users') return 'users';
  if (prefix === 'assets') return 'assets';
  if (prefix === 'sites') return 'sites';
  if (prefix === 'zones') return 'zones';
  if (prefix === 'incidents') return 'incidents';
  if (prefix === 'reports') return 'reports';
  if (prefix === 'documents') return 'documents';
  if (prefix === 'custom-fields') return 'customFields';
  if (prefix === 'partners') return 'partners';
  if (prefix === 'maintenance') return 'maintenance';
  if (prefix === 'diagnostic-rules') return 'diagnosticRules';
  if (KNOWLEDGE_BASE_PREFIXES.has(prefix)) return 'knowledgeBase';
  if (PLATFORM_PREFIXES.has(prefix)) return 'platform';

  return 'platform';
}

export function groupPermissionsByCategory(
  permissions: PermissionDto[],
): GroupedPermissions[] {
  const buckets = new Map<PermissionCategory, PermissionDto[]>();

  for (const category of PERMISSION_CATEGORY_ORDER) {
    buckets.set(category, []);
  }

  for (const permission of permissions) {
    const category = getPermissionCategory(permission.key);
    buckets.get(category)?.push(permission);
  }

  return PERMISSION_CATEGORY_ORDER.map((category) => ({
    category,
    permissions: (buckets.get(category) ?? []).sort((a, b) =>
      a.key.localeCompare(b.key),
    ),
  })).filter((group) => group.permissions.length > 0);
}

export function permissionKeysEqual(
  left: PermissionKey[],
  right: PermissionKey[],
): boolean {
  if (left.length !== right.length) return false;
  const rightSet = new Set(right);
  return left.every((key) => rightSet.has(key));
}

export function permissionKeyToTranslationPath(key: PermissionKey): string {
  return key.replace(/:/g, '.');
}

export function formatPermissionLabel(
  key: PermissionKey,
  t: TFunction<'authorization'>,
): string {
  return t(`permissions.${permissionKeyToTranslationPath(key)}.label`, {
    defaultValue: key,
  });
}

export function isRolePermissionsEditable(
  role: { isSystemRole: boolean; name: string },
  user: { roleName: string },
): boolean {
  if (!role.isSystemRole) return true;
  if (role.name === SystemRoleName.SUPER_ADMIN) return false;
  return (
    user.roleName === SystemRoleName.SUPER_ADMIN &&
    isEditableSystemRoleName(role.name)
  );
}

export function isRoleNameEditable(role: { isSystemRole: boolean }): boolean {
  return !role.isSystemRole;
}

export function formatRoleAuditUserName(
  user: { firstName: string; lastName: string } | null | undefined,
  fallback: string,
): string {
  if (!user) {
    return fallback;
  }

  const fullName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return fullName || fallback;
}

export function resolveSystemRoleLabel(
  name: string,
  t: TFunction<'authorization'>,
): string {
  if (isSystemRoleName(name)) {
    return t(`systemRoleNames.${name}.label`);
  }
  return name;
}

export function resolveSystemRoleDescription(
  name: string,
  t: TFunction<'authorization'>,
): string | undefined {
  if (isSystemRoleName(name)) {
    return t(`systemRoleNames.${name}.description`);
  }
  return undefined;
}

export function filterRolesVisibleToCaller<
  T extends { isSystemRole: boolean; name: string },
>(roles: T[], isSuperAdmin: boolean): T[] {
  return roles.filter((role) => {
    if (role.isSystemRole && isHiddenSystemRoleName(role.name)) {
      return false;
    }
    if (
      !isSuperAdmin &&
      role.isSystemRole &&
      role.name === SystemRoleName.SUPER_ADMIN
    ) {
      return false;
    }
    return true;
  });
}

export function splitRolesByType<T extends { isSystemRole: boolean }>(
  roles: T[],
): { systemRoles: T[]; customRoles: T[] } {
  return {
    systemRoles: roles.filter((role) => role.isSystemRole),
    customRoles: roles.filter((role) => !role.isSystemRole),
  };
}

export function toRoleWithPermissionsFromDetail(
  role: RoleDto & { permissionKeys: PermissionKey[] },
): RoleWithPermissionsDto {
  return {
    id: role.id,
    name: role.name,
    isSystemRole: role.isSystemRole,
    permissionKeys: role.permissionKeys,
  };
}
